import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "./ThemeProvider";
import TID from "@/lib/testIds";

const links = [
  { id: "home", label: "Home", tid: TID.navLinkHome },
  { id: "services", label: "Services", tid: TID.navLinkServices },
  { id: "why-us", label: "Why Us", tid: TID.navLinkWhy },
  { id: "testimonials", label: "Testimonials", tid: TID.navLinkTestimonials },
  { id: "faq", label: "FAQ", tid: TID.navLinkFAQ },
  { id: "contact", label: "Contact", tid: TID.navLinkContact },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid={TID.navContainer}
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "py-2 backdrop-blur-xl bg-white/70 dark:bg-[#0B1120]/70 border-b border-slate-200/60 dark:border-white/10 shadow-sm"
          : "py-4 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6">
        <button
          data-testid={TID.navLogo}
          onClick={() => scrollTo("home")}
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/assets/naksec-logo.png"
            alt="Naksec"
            className="w-9 h-9 object-contain"
          />
          <span className="font-display text-lg md:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            Naksec
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.tid}
              onClick={() => scrollTo(l.id)}
              className="px-3.5 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid={TID.navThemeToggle}
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 transition"
          >
            {theme === "dark" ? (
              <Sun className="w-4 h-4 text-amber-400" />
            ) : (
              <Moon className="w-4 h-4 text-slate-700" />
            )}
          </button>
          <Button
            data-testid={TID.navBookCta}
            onClick={() => scrollTo("contact")}
            className="hidden md:inline-flex h-10 px-5 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple text-white font-semibold shadow-elevated hover:opacity-95 btn-ripple"
          >
            Book Consultation
          </Button>
          <button
            data-testid={TID.navMobileToggle}
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10"
            aria-label="Open menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid={TID.navMobilePanel}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mx-6 mt-3 rounded-2xl glass shadow-elevated p-4 flex flex-col"
          >
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.tid}-mobile`}
                onClick={() => {
                  setMobileOpen(false);
                  scrollTo(l.id);
                }}
                className="text-left px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5"
              >
                {l.label}
              </button>
            ))}
            <Button
              onClick={() => {
                setMobileOpen(false);
                scrollTo("contact");
              }}
              className="mt-2 h-11 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple text-white font-semibold"
            >
              Book Consultation
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
